import {
  ArrowLeftOutlined,
  DeleteOutlined,
  MinusCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { gql, useMutation } from '@apollo/client';
import {
  Button,
  Divider,
  Form,
  Input,
  notification,
  Space,
  Typography,
} from 'antd';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import CSPage from '../../../components/CSPage';
import CSPageHeader from '../../../components/CSPageHeader';
import {
  createRoleAction,
  updateRoleAction,
} from '../../../redux-store/roles-store';
import useIsCreating from '../../../shared/use-is-creating';
import useOne from '../../../shared/use-one';
import {
  createRoleMutation,
  roleQuery,
  updateRoleMutation,
} from '../constants';
import DeleteRoleModal from '../DeleteRoleModal';

const { Text } = Typography;

function EditRole() {
  const history = useHistory();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [updateRole] = useMutation(updateRoleMutation);
  const [createRole] = useMutation(createRoleMutation);
  const dispatch = useDispatch();

  const { _id } = useParams();
  const queryId = useMemo(() => {
    return Math.round(Math.random() * 10000);
  }, []);

  const fetchResult = useOne(
    roleQuery,
    (data) => data && data.role,
    updateRoleAction,
    'roles',
    _id,
    queryId,
  );

  const [form] = Form.useForm();
  const labelInput = useRef(null);
  const [role, setRole] = useState();

  const onlyOnce = useRef(false);
  useEffect(() => {
    if (!fetchResult.loading && !onlyOnce.current && fetchResult.data) {
      setRole(fetchResult.data);
      onlyOnce.current = true;
    }
  }, [fetchResult, loading]);

  useEffect(() => {
    if (role) {
      const { ...otherFields } = role;
      form.setFieldsValue(otherFields);
    }
  }, [form, role]);

  useLayoutEffect(() => {
    if (labelInput.current) {
      labelInput.current.focus();
    }
  }, []);

  const handleBack = useCallback(() => history.goBack(), [history]);

  const onSubmit = useCallback(
    async (_values) => {
      const { permissions, ...values } = _values;
      setLoading(true);
      setError(null);
      try {
        if (role) {
          const result = await updateRole({
            variables: {
              role: {
                _id,
                ...values,
                permissions: permissions || [],
              },
            },
          });
          dispatch(updateRoleAction(result.data.updateRole.role));
          notification.success({
            message: 'Saved',
            description: 'Role updated successfully',
          });
        } else {
          const result = await createRole({
            variables: {
              role: {
                _id,
                ...values,
                permissions: permissions || [],
              },
            },
          });
          dispatch(createRoleAction(result.data.createRole.role));
          notification.success({
            message: 'Saved',
            description: 'Role created successfully',
          });
        }
        history.push('/roles');
      } catch (err) {
        console.error(err);
        setError(err.message);
      }
      setLoading(false);
    },
    [role, _id, history, createRole, updateRole, dispatch],
  );

  const { title, showDelete } = useIsCreating(
    location,
    role,
    loading,
    'Create New Role',
    'Edit Role',
  );

  return (
    <>
      <CSPage>
        <CSPageHeader
          title={title}
          backActions={[
            <Link key="back" to="/roles">
              <Button type="text" icon={<ArrowLeftOutlined />}>
                Back to Roles
              </Button>
            </Link>,
          ]}
        />
        <Form
          layout="vertical"
          onFinish={onSubmit}
          id="editRole"
          form={form}
          style={{ maxWidth: 500 }}
        >
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: 'Please enter a name' }]}
          >
            <Input ref={labelInput} disabled={loading} />
          </Form.Item>
          <Form.Item
            label="Description"
            name="desc"
            rules={[{ required: true, message: 'Please enter a description' }]}
          >
            <Input disabled={loading} />
          </Form.Item>
          <Form.Item label="Permissions">
            <Form.List name="permissions">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, index) => (
                    <Form.Item key={field.key}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%',
                        }}
                      >
                        <Form.Item
                          {...field}
                          validateTrigger={['onChange', 'onBlur']}
                          rules={[
                            {
                              required: true,
                              message: 'Please enter a permission',
                            },
                          ]}
                          noStyle
                        >
                          <Input
                            placeholder="some-permission:some-_id"
                            style={{ flex: 1, marginRight: 8 }}
                          />
                        </Form.Item>
                        <MinusCircleOutlined
                          className="dynamic-delete-button"
                          onClick={() => remove(field.name)}
                        />
                      </div>
                    </Form.Item>
                  ))}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      icon={<PlusOutlined />}
                    >
                      Add permission
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldsError }) => {
              const errorList = getFieldsError();
              let showError = false;
              errorList.forEach((errors) => {
                if (errors.errors.length) {
                  showError = true;
                }
              });
              return (
                showError && (
                  <Text type="danger" style={{ marginTop: 16 }}>
                    Please correct the errors above
                  </Text>
                )
              );
            }}
          </Form.Item>
          <div style={{ height: 16 }} />
          <Form.Item>
            <Space
              style={{
                width: '100%',
                justifyContent: 'space-between',
              }}
            >
              <Button
                key="cancel"
                onClick={handleBack}
                htmlType="button"
                type="text"
                size="small"
                disabled={loading}
                icon={<ArrowLeftOutlined />}
                style={{ marginLeft: -7 }}
              >
                Cancel
              </Button>
              <Button
                key="send"
                type="primary"
                loading={loading}
                htmlType="submit"
              >
                Save Role
              </Button>
            </Space>
          </Form.Item>
          {error && (
            <div
              className="server-error ant-form-item-has-error"
              style={{ marginTop: 16 }}
            >
              <div className="ant-form-item-explain">{error}</div>
            </div>
          )}
          {showDelete && (
            <>
              <Divider />
              <div className="delete-box">
                <Space align="center">
                  <Button
                    onClick={() => setDeleting(role)}
                    icon={<DeleteOutlined />}
                    danger
                    ghost
                  >
                    Delete Role
                  </Button>
                </Space>
              </div>
            </>
          )}
        </Form>
        <div style={{ minHeight: 300 }} />
        <DeleteRoleModal setRole={setDeleting} role={deleting} />
      </CSPage>
      <style jsx>{`
        .delete-box {
          display: flex;
          justify-content: center;
        }
      `}</style>
    </>
  );
}

export default EditRole;
